import * as angular from 'angular';
import * as _ from 'lodash';

import { getOwnerUsername } from '@mode/shared/util-js';
import { ToastService } from '@mode/shared/util-toast';
import preview from './categorical-palette-preview.html';
import { COLOR_PALETTE_TYPES, DEFAULT_PALETTE_PREVIEW_COLOR, DIVERGENT_PALETTE_MIDDLE_COLOR } from './constants';

const colorHexRegex = /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)|(^[0-9A-F]{6}$)|(^[0-9A-F]{3}$)/i;
const username = getOwnerUsername(window.document, true);

class EditColorPaletteController implements angular.IController {
  token: any;
  account: any;
  paletteTypes = COLOR_PALETTE_TYPES;
  palette: any;
  startColor?: string;
  endColor?: string;
  middleColor?: string;
  linearGradient?: string;
  defaultPreviewColor = DEFAULT_PALETTE_PREVIEW_COLOR;

  static $inject = ['$element', 'ColorPaletteModel', 'AccountModel', 'ToastService'];

  constructor(private $element, private ColorPaletteModel, private AccountModel, private alertService: ToastService) {}

  $onInit() {
    if (!this.account) {
      this.getAccount(username);
    }
    if (this.token) {
      this.getColorPalette(this.token);
    } else {
      this.palette = {
        palette_type: 'categorical',
      };
    }
  }

  updateDefaultColors() {
    switch (this.palette.palette_type) {
      case 'categorical':
        this.palette.value = '';
        break;
      case 'divergent':
        this.startColor = '';
        this.middleColor = '';
        this.endColor = '';
        this.palette.value = '';
        break;
      case 'sequential':
        this.startColor = '';
        this.endColor = '';
        this.palette.value = '';
        break;
    }
  }

  getAccount(username) {
    this.AccountModel.find({
      username: username,
      useEntryPath: true,
      refresh: true,
      includes: ['new_color_palette'],
    }).then((account) => {
      this.account = account;
    });
  }

  getColorPalette(token) {
    this.ColorPaletteModel.find({
      account: username,
      token: this.token,
      useEntryPoint: true,
    }).then((palette) => {
      this.palette = palette;
      this.$element.find('#color-input').tokenfield('setTokens', palette.value);
      if (this.palette.palette_type === 'divergent') {
        const colors = this.palette.value.split(',');
        this.startColor = _.trim(_.first(colors));
        this.endColor = _.trim(_.last(colors));
        this.middleColor = _.trim(colors[1]);

        this.setLinearGradient();
      } else if (this.palette.palette_type === 'sequential') {
        const colors = this.palette.value.split(',');
        this.startColor = _.trim(_.first(colors));
        this.endColor = _.trim(_.last(colors));

        this.setLinearGradient();
      }
    });
  }

  paletteLength() {
    if (!this.palette || !this.palette.value) {
      return 0;
    }
    return this.palette.value.split(',').length;
  }

  paletteColorAtIndex(idx) {
    if (!this.palette || !this.palette.value) {
      return DEFAULT_PALETTE_PREVIEW_COLOR;
    }
    return this.palette.value.split(',')[idx] || DEFAULT_PALETTE_PREVIEW_COLOR;
  }

  paletteIsValid() {
    if (!this.palette) {
      return false;
    }
    if (!this.palette.name) {
      return false;
    }
    if (this.palette.palette_type === 'categorical') {
      if (!this.palette.value) {
        return false;
      }
      return this.palette.name.length && this.palette.value.split(',').length > 1;
    }
    if (this.palette.palette_type === 'divergent') {
      return (
        this.palette.name.length &&
        colorHexRegex.test(this.startColor) &&
        colorHexRegex.test(this.middleColor) &&
        colorHexRegex.test(this.endColor)
      );
    }
    if (this.palette.palette_type === 'sequential') {
      return this.palette.name.length && colorHexRegex.test(this.startColor) && colorHexRegex.test(this.endColor);
    }
  }

  saveColorPalette() {
    if (this.palette.palette_type === 'divergent') {
      this.palette.value = this.startColor + ', ' + this.middleColor + ', ' + this.endColor;
    } else if (this.palette.palette_type === 'sequential') {
      this.palette.value = this.startColor + ', ' + this.endColor;
    }
    if (this.palette.token) {
      this.ColorPaletteModel.update(this.palette)
        .then((newPalette) => {
          window.location.href = this.account.colorPalettesLink;
        })
        .catch((e) => {
          this.alertService.error('Error saving palette');
        });
    } else {
      this.ColorPaletteModel.create({
        account: username,
        params: {
          'color_palette.name': this.palette.name,
          'color_palette.palette_type': this.palette.palette_type,
          'color_palette.value': this.palette.value,
        },
      })
        .then((newPalette) => {
          window.location.href = this.account.colorPalettesLink;
        })
        .catch((e) => {
          this.alertService.error('Error saving palette');
        });
    }
  }

  colorAdded(evt, tokens) {
    const valid: boolean = colorHexRegex.test(evt.attrs.value);
    const target: HTMLElement = evt.relatedTarget;

    if (!valid) {
      target.classList.add('invalid');
    } else {
      angular.element(target).prepend(`
        <span class="swatch-container">
          <span class="swatch" style="background-color: ${evt.attrs.value}"></span>
        </span>
      `);
    }
  }

  colorCreating(evt, tokens) {
    const valid = colorHexRegex.test(evt.attrs.value);
    if (!valid) {
      evt.preventDefault();
      return false;
    } else {
      if (evt.attrs.value.charAt(0) !== '#') {
        evt.attrs.value = '#' + evt.attrs.value;
        evt.attrs.label = evt.attrs.value;
      }
    }
  }

  updateStartColor() {
    if (this.startColor.charAt(0) !== '#') {
      this.startColor = '#' + this.startColor;
    }
    const valid = colorHexRegex.test(this.startColor);
    if (valid) {
      this.setLinearGradient();
      return;
    } else {
      this.startColor = '';
    }
  }

  updateMiddleColor() {
    if (this.middleColor.charAt(0) !== '#') {
      this.middleColor = '#' + this.middleColor;
    }
    const valid = colorHexRegex.test(this.middleColor);
    if (valid) {
      this.setLinearGradient();
      return;
    } else {
      this.middleColor = '';
    }
  }

  updateEndColor() {
    if (this.endColor.charAt(0) !== '#') {
      this.endColor = '#' + this.endColor;
    }
    const valid = colorHexRegex.test(this.endColor);
    if (valid) {
      this.setLinearGradient();
      return;
    } else {
      this.endColor = '';
    }
  }

  setLinearGradient() {
    if (this.palette.palette_type === 'divergent') {
      this.linearGradient =
        'linear-gradient(to right, ' +
        (this.startColor || DEFAULT_PALETTE_PREVIEW_COLOR) +
        ', ' +
        (this.middleColor || DIVERGENT_PALETTE_MIDDLE_COLOR) +
        ', ' +
        (this.endColor || DEFAULT_PALETTE_PREVIEW_COLOR) +
        ')';
    } else if (this.palette.palette_type === 'sequential') {
      this.linearGradient =
        'linear-gradient(to right, ' +
        (this.startColor || DEFAULT_PALETTE_PREVIEW_COLOR) +
        ', ' +
        (this.endColor || DEFAULT_PALETTE_PREVIEW_COLOR) +
        ')';
    }
  }
}

const EditColorPaletteComponent: angular.IComponentOptions = {
  bindings: {
    token: '@',
    account: '<?',
  },
  controller: EditColorPaletteController,
  controllerAs: 'ctrl',
  template: `
    <label class="control-label">Details</label>
    <div class="details-container">
      <span class="input-label">Name</span>
      <input type="text" class="form-control" ng-model="ctrl.palette.name" />
      <span class="input-label">Palette Type</span>
      <select
        class="form-control"
        ng-model="ctrl.palette.palette_type"
        ng-options="type.id as type.name for type in ctrl.paletteTypes"
        ng-change="ctrl.updateDefaultColors()"
        ng-disabled="ctrl.token"
      ></select>
    </div>

    <br />
    <label class="control-label">Color Values</label>
    <div class="color-values-container">
      <div class="categorical-container" ng-show="ctrl.palette.palette_type === 'categorical'">
        <div>
          <div class="input-label">Hex Codes (2-20)</div>
          <input
            id="color-input"
            type="text"
            class="form-control form-control-sm"
            ng-model="ctrl.palette.value"
            autocomplete="off"
            tokenfield
            writable="true"
            on-token-creating="ctrl.colorCreating($event, $tokens)"
            on-token-created="ctrl.colorAdded($event, $tokens)" />
        </div>
        <div class="svg-preview-container" >
          <div class="svg-preview">
            ${preview}
          </div>
        </div>
      </div>

      <div class="divergent-container" ng-if="ctrl.palette.palette_type !== 'categorical'">
        <div class="divergent-inputs" ng-class="ctrl.palette.palette_type">
          <div class="input-label left" >
            <div>Hex</div>
            <div class="input-container left">
              <div class="swatch-container">
                <span
                  class="divergent-color-swatch"
                  ng-style="{'background-color': ctrl.startColor}">
                </span>
              </div>
              <input
                id="color-input-1"
                type="text"
                spellcheck="false"
                class="form-control form-control-sm"
                ng-blur="ctrl.updateStartColor()"
                ng-model="ctrl.startColor" />
            </div>
          </div>


          <div class="input-label middle" ng-if="ctrl.palette.palette_type === 'divergent'">
            <div>Hex</div>
            <div class="input-container middle">
              <div class="swatch-container">
                <span
                  class="divergent-color-swatch"
                  ng-style="{'background-color': ctrl.middleColor}">
                </span>
              </div>
              <input
                id="color-input-2"
                type="text"
                spellcheck="false"
                class="form-control form-control-sm"
                ng-blur="ctrl.updateMiddleColor()"
                ng-model="ctrl.middleColor" />
            </div>
          </div>

          <div class="input-label right" >
            <div>Hex</div>
            <div class="input-container right">
            <div class="swatch-container">
              <span
                class="divergent-color-swatch"
                ng-style="{'background-color': ctrl.endColor}">
              </span>
            </div>

            <input
              id="color-input-3"
              type="text"
              spellcheck="false"
              class="form-control form-control-sm"
              ng-blur="ctrl.updateEndColor()"
              ng-model="ctrl.endColor" />
          </div>
          </div>
        </div>

        <div class="gradient-container">
          <span class="divergent-gradient"
            ng-style="{'background': ctrl.linearGradient}">
          </span>
        </div>
      </div>
      <capra-button
        class="pull-right save-palette"
        text="Save"
        [disabled]="!ctrl.paletteIsValid()"
        (clicked)="ctrl.saveColorPalette()"
      ></capra-button>
      <span class="clearfix"></span>
    </div>
  `,
};

export { EditColorPaletteComponent };
